export interface CatalogImportItem {
  number: string;
  name: string;
  long_description: string;
  catalog_type: string;
  factor: number;
  unit_price_cent: number;
}

export function parseCatalogFile(text: string, fileName = ""): CatalogImportItem[] {
  const trimmed = text.replace(/^\uFEFF/, "").trim();
  if (!trimmed) throw new Error("Die Importdatei ist leer.");
  if (fileName.toLowerCase().endsWith(".json") || trimmed.startsWith("[")) {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!Array.isArray(parsed)) throw new Error("Die JSON-Datei muss eine Liste enthalten.");
    return parsed.map((entry, index) => normalizeObject(entry, index + 1));
  }
  return parseCsv(trimmed);
}

function parseCsv(text: string): CatalogImportItem[] {
  const delimiter = detectDelimiter(text);
  const rows = readDelimited(text, delimiter);
  if (rows.length < 2) throw new Error("Die CSV-Datei enthält keine Datenzeilen.");
  const headers = rows[0]!.map(normalizeHeader);
  const numberIndex = findHeader(headers, ["nummer", "code", "number"]);
  const nameIndex = findHeader(headers, ["kurzbeschreibung", "bezeichnung", "name"]);
  if (numberIndex < 0 || nameIndex < 0)
    throw new Error("Die CSV benötigt die Spalten „Nummer“ und „Kurzbeschreibung“.");
  return rows.slice(1)
    .filter((row) => row.some((cell) => cell.trim()))
    .map((row, index) => normalizeObject({
      number: row[numberIndex],
      name: row[nameIndex],
      long_description: valueFor(row, headers, ["langbeschreibung", "beschreibung", "longdescription"]),
      catalog_type: valueFor(row, headers, ["kategorie", "katalog", "catalogtype"]),
      factor: valueFor(row, headers, ["faktor", "factor"]),
      price: valueFor(row, headers, ["preisineuro", "preis", "price", "unitprice"]),
      unit_price_cent: valueFor(row, headers, ["preisincent", "unitpricecent"]),
    }, index + 2));
}

function normalizeObject(value: unknown, row: number): CatalogImportItem {
  if (!value || typeof value !== "object") throw new Error(`Zeile ${row}: ungültiger Eintrag.`);
  const entry = value as Record<string, unknown>;
  const number = String(entry.number ?? entry.nummer ?? entry.code ?? "").trim();
  const name = String(entry.name ?? entry.kurzbeschreibung ?? entry.bezeichnung ?? "").trim();
  if (!number || !name) throw new Error(`Zeile ${row}: Nummer und Kurzbeschreibung sind erforderlich.`);
  const factor = parseDecimal(entry.factor ?? entry.faktor ?? 1);
  const centValue = entry.unit_price_cent ?? entry.preis_in_cent;
  const unitPriceCent = centValue !== undefined && centValue !== ""
    ? Math.round(parseDecimal(centValue))
    : Math.round(parseDecimal(entry.price ?? entry.preis ?? 0) * 100);
  if (!Number.isFinite(factor) || factor <= 0) throw new Error(`Zeile ${row}: Faktor ist ungültig.`);
  if (!Number.isFinite(unitPriceCent) || unitPriceCent < 0) throw new Error(`Zeile ${row}: Preis ist ungültig.`);
  return {
    number,
    name,
    long_description: String(entry.long_description ?? entry.langbeschreibung ?? entry.beschreibung ?? "").trim(),
    catalog_type: String(entry.catalog_type ?? entry.kategorie ?? entry.katalog ?? "custom").trim() || "custom",
    factor,
    unit_price_cent: unitPriceCent,
  };
}

function parseDecimal(value: unknown) {
  const normalized = String(value ?? "").trim().replace(/\s/g, "").replace(",", ".");
  return Number(normalized);
}

function detectDelimiter(text: string) {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  return (firstLine.match(/;/g)?.length ?? 0) >= (firstLine.match(/,/g)?.length ?? 0) ? ";" : ",";
}

function readDelimited(text: string, delimiter: string) {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < text.length; index++) {
    const character = text[index]!;
    if (character === '"') {
      if (quoted && text[index + 1] === '"') { cell += '"'; index++; }
      else quoted = !quoted;
    } else if (character === delimiter && !quoted) {
      row.push(cell); cell = "";
    } else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && text[index + 1] === "\n") index++;
      row.push(cell); rows.push(row); row = []; cell = "";
    } else cell += character;
  }
  row.push(cell); rows.push(row);
  return rows;
}

function normalizeHeader(value: string) {
  return value.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
}
function findHeader(headers: string[], names: string[]) {
  return headers.findIndex((header) => names.includes(header));
}
function valueFor(row: string[], headers: string[], names: string[]) {
  const index = findHeader(headers, names);
  return index >= 0 ? row[index] ?? "" : "";
}

